/**
 * Error Interceptor
 * Handles HTTP errors globally and shows user-visible notifications
 * Angular 19+ functional interceptor
 */

import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { ConsentPopupService } from '../services/consent-popup.service';
import { ToastService } from '../services/toast.service';
import { RouteBuilder } from '../constants/routes.constants';

// URLs that should not show toast notifications (silent failures)
const SILENT_ERROR_URLS = [
  '/user-preferences',
  '/auth/',
  '/actuator/',
  '/internal/',
];

// Status codes that SHOULD show toast notifications
// Only show errors for critical issues: server errors (500), timeout (0)
// 401 is handled separately with redirect and has its own toast
const TOAST_VISIBLE_STATUS_CODES = [
  0, // Client-side/network error (timeout, no connection)
  500, // Internal server error
];

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const consentPopupService = inject(ConsentPopupService);
  const toastService = inject(ToastService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      let errorMessage = 'An error occurred';
      let shouldShowToast = true;

      // Check if this URL should fail silently
      const isSilentUrl = SILENT_ERROR_URLS.some((url) =>
        req.url.includes(url),
      );
      // Only show toast for critical errors (500, timeout)
      const shouldShowErrorToast = TOAST_VISIBLE_STATUS_CODES.includes(
        error.status,
      );

      if (error.error instanceof ErrorEvent) {
        // Client-side error (network error, timeout, etc.)
        errorMessage = `Error: ${error.error.message}`;

        // Show connection error toast only for critical errors
        if (!isSilentUrl && shouldShowErrorToast) {
          toastService.httpError(0, 'Error de conexión');
        }
        shouldShowToast = false; // Already shown or not needed
      } else {
        // Server-side error
        errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;

        // Handle 401 Unauthorized errors
        if (error.status === 401) {
          console.warn('Unauthorized access - token may be invalid or expired');

          // IMPORTANT: Don't clear data if consent is being saved
          // This prevents race condition where 401 clears the token before consent save completes
          if (consentPopupService.isSavingConsent()) {
            console.log(
              '🍪 401 received but consent save in progress - NOT clearing user data',
            );
            return throwError(() => error);
          }

          // Check if user has accepted policy (has token or temporal user context)
          const hasToken = !!authService.getAuthToken();
          const userContext = authService.userContext();
          const hasPolicyAcceptance =
            !!userContext?.policyAcceptance || !!userContext?.sessionId;

          // If user has token or has accepted policy, clear all user data
          if (hasToken || hasPolicyAcceptance) {
            // Clear all user data from local storage
            authService.clearUserDataOnUnauthorized();

            // Show toast for session expiration (except on auth endpoints)
            if (!req.url.includes('/auth/')) {
              toastService.warning(
                'Your session has expired. Please sign in again.',
                {
                  title: 'Session Expired',
                },
              );
            }

            // Don't redirect on authentication endpoints or user-preferences endpoint
            // (to avoid interrupting login/registration/policy acceptance flows)
            if (
              !req.url.includes('/auth/') &&
              !req.url.includes('/user-preferences')
            ) {
              // Redirect to offers page after clearing data
              router.navigate([RouteBuilder.offers()], { replaceUrl: true });
            }
          }
          shouldShowToast = false; // Already handled
        }

        // Show toast only for critical errors (500, timeout) if not silent URL
        if (shouldShowToast && !isSilentUrl && shouldShowErrorToast) {
          // Extract server error message if available
          const serverMessage = extractServerMessage(error);
          toastService.httpError(error.status, serverMessage);
        }

        // Log specific status codes
        switch (error.status) {
          case 403:
            console.warn('Forbidden - insufficient permissions');
            break;
          case 404:
            console.warn('Resource not found');
            break;
          case 500:
            console.error('Internal server error');
            break;
          case 502:
          case 503:
          case 504:
            console.error('Server unavailable:', error.status);
            break;
        }
      }

      console.error('HTTP Error:', errorMessage);
      return throwError(() => error);
    }),
  );
};

/**
 * Extract a user-friendly error message from the server response
 */
function extractServerMessage(error: HttpErrorResponse): string | undefined {
  if (!error.error) return undefined;

  // Check for common error response formats
  if (typeof error.error === 'string') {
    return error.error;
  }

  if (error.error.message) {
    return error.error.message;
  }

  if (error.error.error) {
    return error.error.error;
  }

  if (error.error.detail) {
    return error.error.detail;
  }

  return undefined;
}
